import { useState } from "react";

import { Box, Flex, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { InputLabel, NumberInput, Select, Text, TextInput, YesNoRadioButton } from "components";
import SectionTitle, { SectionTitleIndicatorType } from "pages/clients/view/components/section-title";
import {
  comparisonValuesDiffer,
  hasComparisonValue,
  normalizeComparisonValue,
} from "pages/clients/view/credit/request/preliminary-comparison";
import PreliminaryComparisonModal, {
  PreliminaryComparisonRow,
} from "pages/clients/view/credit/request/preliminary-comparison-modal";
import { CreditOptionsResponse, CreditRequestForm, CreditResponse } from "types";

import { CreditComparisonSource } from "./comparison-source";

interface CreditRequestRequestProps {
  form: UseFormReturnType<CreditRequestForm>;
  options: CreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
  comparisonSource?: CreditComparisonSource | null;
}

type ComparisonFieldConfig = {
  name: keyof CreditRequestForm;
  label: string;
  formatValue?: (value: any) => string;
};

const CreditRequestRequest = ({
  form,
  options,
  visibleFields,
  readOnly = false,
  comparisonSource,
}: CreditRequestRequestProps) => {
  const { t } = useTranslation();
  const currentValues = form.getValues();
  const comparisonCredit = comparisonSource?.credit;
  const comparisonIndicatorType = comparisonSource?.type === "previous" ? "history" : "warning";
  const canApplyComparisonSource = !!comparisonSource;
  const [comparisonOpened, setComparisonOpened] = useState(false);

  const formatYesNoValue = (value: boolean | null) =>
    value === true ? t("yes") : value === false ? t("no") : "-";

  const requestFields: ComparisonFieldConfig[] = [
    { name: "creditAmount", label: t("credit.creditAmount.label") },
    { name: "paymentPeriod", label: t("credit.paymentPeriod.label") },
    {
      name: "monthlyInstallmentFrom",
      label: `${t("credit.monthlyInstallment.label")} (${t("credit.monthlyInstallment.from")})`,
    },
    {
      name: "monthlyInstallmentTo",
      label: `${t("credit.monthlyInstallment.label")} (${t("credit.monthlyInstallment.to")})`,
    },
    {
      name: "creditInsurance",
      label: t("credit.creditInsurance.label"),
      formatValue: formatYesNoValue,
    },
    {
      name: "creditType",
      label: t("credit.creditType.label"),
      formatValue: (value) => (value ? t(`credit.creditType.options.${value}`) : "-"),
    },
    {
      name: "creditPurpose",
      label: t("credit.creditPurpose.label"),
      formatValue: (value) => (value ? t(`credit.creditPurpose.options.${value}`) : "-"),
    },
    { name: "creditPurposeNote", label: t("credit.creditPurposeNote.label") },
    {
      name: "existingMonthlyInstallment",
      label: t("credit.existingMonthlyInstallment.label"),
    },
    {
      name: "bank",
      label: t("credit.bank.label"),
      formatValue: (value) => (value ? t(`credit.bank.options.${value}`) : "-"),
    },
    { name: "bankName", label: t("credit.bankName.label") },
    {
      name: "refinanceCreditCard",
      label: t("credit.refinanceCreditCard.label"),
      formatValue: formatYesNoValue,
    },
    {
      name: "refinanceLease",
      label: t("credit.refinanceLease.label"),
      formatValue: formatYesNoValue,
    },
  ];

  const hasRequestData = requestFields.some((field) =>
    hasComparisonValue(currentValues[field.name]),
  );

  const comparisonHasRequestData = requestFields.some((field) =>
    hasComparisonValue((comparisonCredit as CreditResponse | undefined)?.[field.name as keyof CreditResponse]),
  );

  const fieldValuesDiffer = (field: ComparisonFieldConfig) =>
    comparisonValuesDiffer(
      (comparisonCredit as CreditResponse | undefined)?.[field.name as keyof CreditResponse],
      currentValues[field.name],
    );

  const requestWarning =
    !!comparisonCredit &&
    (hasRequestData || comparisonHasRequestData) &&
    requestFields.some(fieldValuesDiffer);

  const comparisonRows: PreliminaryComparisonRow[] =
    comparisonOpened && comparisonCredit
      ? requestFields
          .filter(fieldValuesDiffer)
          .map((field) => {
            const formatValue =
              field.formatValue ??
              ((value: any) => String(normalizeComparisonValue(value) ?? "-"));

            return {
              key: String(field.name),
              label: field.label,
              source: <Text>{formatValue(comparisonCredit[field.name as keyof CreditResponse])}</Text>,
              current: <Text>{formatValue(currentValues[field.name])}</Text>,
            };
          })
      : [];

  const applyComparisonValues = () => {
    if (!comparisonCredit) {
      return;
    }

    requestFields
      .filter(fieldValuesDiffer)
      .forEach((field) => {
        form.setFieldValue(
          field.name,
          (comparisonCredit[field.name as keyof CreditResponse] ?? undefined) as any,
        );
      });

    setComparisonOpened(false);
  };

  return (
    <Box p={"md"}>
      <Box mb={40}>
        <SectionTitle
          label={t("credit.aboutCredit.title")}
          hasWarning={requestWarning}
          onWarningClick={() => setComparisonOpened(true)}
          indicatorType={comparisonIndicatorType as SectionTitleIndicatorType}
        />
        <Box pt={"xs"} mt={"sm"}>
          <SimpleGrid cols={4}>
            <NumberInput
              label={t('credit.creditAmount.label')}
              key={form.key('creditAmount')}
              {...form.getInputProps('creditAmount')}
              min={options['creditAmount']?.min}
              max={options['creditAmount']?.max}
              required={true}
              disabled={readOnly}
            />
            <NumberInput
              label={t('credit.paymentPeriod.label')}
              key={form.key('paymentPeriod')}
              {...form.getInputProps('paymentPeriod')}
              suffix={undefined}
              min={options['paymentPeriod']?.min}
              max={options['paymentPeriod']?.max}
              allowDecimal={false}
              required={true}
              disabled={readOnly}
            />
            <Box>
              <InputLabel label={t('credit.monthlyInstallment.label')} required={true} />
              <Flex align={'center'} justify={'space-evenly'}>
                <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                  {t('credit.monthlyInstallment.from')}
                </Text>
                <NumberInput
                  key={form.key('monthlyInstallmentFrom')}
                  {...form.getInputProps('monthlyInstallmentFrom')}
                  suffix={undefined}
                  min={options['existingMonthlyInstallment']?.min}
                  max={options['existingMonthlyInstallment']?.max}
                  disabled={true}
                  w={'30%'}
                />
                <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                  {t('credit.monthlyInstallment.to')}
                </Text>
                <NumberInput
                  key={form.key('monthlyInstallmentTo')}
                  {...form.getInputProps('monthlyInstallmentTo')}
                  suffix={undefined}
                  min={options['existingMonthlyInstallment']?.min}
                  max={options['existingMonthlyInstallment']?.max}
                  disabled={true}
                  w={'30%'}
                />
                <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                  {'CHF'}
                </Text>
              </Flex>
            </Box>
            <YesNoRadioButton
              label={t('credit.creditInsurance.label')}
              name={'creditInsurance'}
              form={form}
              required={true}
              disabled={readOnly}
            />
          </SimpleGrid>
          <SimpleGrid cols={4} mt={"sm"}>
            <Select
              label={t('credit.creditType.label')}
              data={options['creditType'].map((el) => ({
                value: el,
                label: t(`credit.creditType.options.${el}`),
              }))}
              key={form.key('creditType')}
              {...form.getInputProps('creditType')}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
            {visibleFields.includes('creditPurpose') && (
              <Select
                label={t('credit.creditPurpose.label')}
                data={options['creditPurpose'].map((el) => ({
                  value: el,
                  label: t(`credit.creditPurpose.options.${el}`),
                }))}
                key={form.key('creditPurpose')}
                {...form.getInputProps('creditPurpose')}
                clearable={true}
                required={true}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes('creditPurposeNote') && (
              <TextInput
                label={t('credit.creditPurposeNote.label')}
                key={form.key('creditPurposeNote')}
                {...form.getInputProps('creditPurposeNote')}
                style={{ verticalAlign: 'top' }}
                required={true}
                disabled={readOnly}
              />
            )}
          </SimpleGrid>
        </Box>
      </Box>

      <Box mt={"md"}>
        <SimpleGrid cols={4}>
          {visibleFields.includes('existingMonthlyInstallment') && (
            <NumberInput
              label={t('credit.existingMonthlyInstallment.label')}
              key={form.key('existingMonthlyInstallment')}
              min={options['existingMonthlyInstallment']?.min}
              max={options['existingMonthlyInstallment']?.max}
              {...form.getInputProps('existingMonthlyInstallment')}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('bank') && (
            <Select
              label={t('credit.bank.label')}
              data={options['bank'].map((el) => ({
                value: el,
                label: t(`credit.bank.options.${el}`),
              }))}
              key={form.key('bank')}
              {...form.getInputProps('bank')}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('bankName') && (
            <TextInput
              label={t('credit.bankName.label')}
              key={form.key('bankName')}
              {...form.getInputProps('bankName')}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('refinanceCreditCard') && (
            <YesNoRadioButton
              label={t('credit.refinanceCreditCard.label')}
              name={'refinanceCreditCard'}
              form={form}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('refinanceLease') && (
            <YesNoRadioButton
              label={t('credit.refinanceLease.label')}
              name={'refinanceLease'}
              form={form}
              required={true}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>
      </Box>
      <Box mt={"sm"}>
        <SimpleGrid cols={4}>
          <TextInput
            label={t('credit.comment.singular')}
            key={form.key('comment1')}
            {...form.getInputProps('comment1')}
            disabled={readOnly}
          />
        </SimpleGrid>
      </Box>

      <PreliminaryComparisonModal
        opened={comparisonOpened}
        onClose={() => setComparisonOpened(false)}
        onApply={applyComparisonValues}
        title={t(
          comparisonSource?.type === "previous"
            ? "credit.previousComparison.title"
            : "credit.preliminaryComparison.title",
        )}
        emptyText={t("credit.preliminaryComparison.empty")}
        fieldLabel={t("credit.preliminaryComparison.field")}
        sourceLabel={t(
          comparisonSource?.type === "previous"
            ? "credit.previousComparison.previous"
            : "credit.preliminaryComparison.preliminary",
        )}
        currentLabel={t("credit.preliminaryComparison.active")}
        applyLabel={t(
          comparisonSource?.type === "previous"
            ? "credit.previousComparison.apply"
            : "credit.preliminaryComparison.apply",
        )}
        cancelLabel={t("credit.preliminaryComparison.cancel")}
        rows={comparisonRows}
        showApplyButton={canApplyComparisonSource}
      />
    </Box>
  );
};

export default CreditRequestRequest;
